'use client';

import { type Editor, type EditorStateSnapshot, useEditorState } from '@tiptap/react';
import { forwardRef, useCallback, useState } from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';
import { isExtensionAvailable } from '@/lib/tiptap-utils';

// --- Icons ---
import { ChevronDownIcon } from '@/components/tiptap-editor/tiptap-icons/chevron-down-icon';

// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/tiptap-editor/tiptap-ui-primitive/dropdown-menu';

// --- Tiptap UI ---
import { TextAlignButton } from '@/components/tiptap-editor/tiptap-ui/text-align-button';
import {
    TEXT_ALIGN_SHORTCUT_KEYS,
    type TextAlign,
    textAlignIcons,
} from '@/components/tiptap-editor/tiptap-ui/text-align-button/use-text-align';

const ALIGNMENTS: TextAlign[] = ['left', 'center', 'right', 'justify'];

export interface TextAlignDropdownMenuProps extends Omit<ButtonProps, 'type'> {
    /**
     * The Tiptap editor instance.
     */
    editor?: Editor;
    /**
     * The alignments to display in the dropdown.
     * @default ['left', 'center', 'right', 'justify']
     */
    alignments?: TextAlign[];
    /**
     * Whether to render the dropdown menu in a portal
     * @default false
     */
    portal?: boolean;
    /**
     * Callback for when the dropdown opens or closes
     */
    onOpenChange?: (isOpen: boolean) => void;
}

type TextAlignDropdownSnapshot = {
    hasTextAlign: boolean;
    currentAlign: TextAlign;
    isActive: boolean;
};

function textAlignDropdownSnapshotEquality(a: TextAlignDropdownSnapshot, b: TextAlignDropdownSnapshot | null): boolean {
    return a.hasTextAlign === b?.hasTextAlign && a.currentAlign === b?.currentAlign && a.isActive === b?.isActive;
}

export const TextAlignDropdownMenu = forwardRef<HTMLButtonElement, TextAlignDropdownMenuProps>(
    ({ editor: providedEditor, alignments = ALIGNMENTS, portal = true, onOpenChange, ...buttonProps }, ref) => {
        const { editor } = useTiptapEditor(providedEditor);
        const [isOpen, setIsOpen] = useState(false);

        const textAlignDropdownSelector = useCallback(
            (ctx: EditorStateSnapshot<Editor | null>): TextAlignDropdownSnapshot => {
                const ed = ctx.editor;
                if (!ed) {
                    return { hasTextAlign: false, currentAlign: 'left', isActive: false };
                }
                const hasTextAlign = isExtensionAvailable(ed, 'textAlign');
                const currentAlign = alignments.find(a => ed.isActive({ textAlign: a })) ?? 'left';
                return {
                    hasTextAlign,
                    currentAlign,
                    isActive: currentAlign !== 'left',
                };
            },
            [alignments],
        );

        const textAlignToolbar = useEditorState({
            editor,
            selector: textAlignDropdownSelector,
            equalityFn: textAlignDropdownSnapshotEquality,
        });

        const hasTextAlign = textAlignToolbar?.hasTextAlign ?? false;
        const currentAlign = textAlignToolbar?.currentAlign ?? 'left';
        const isActive = textAlignToolbar?.isActive ?? false;
        const TriggerIcon = textAlignIcons[currentAlign];

        const handleOpenChange = useCallback(
            (open: boolean) => {
                if (!editor) return;
                setIsOpen(open);
                onOpenChange?.(open);
            },
            [editor, onOpenChange],
        );

        if (!editor || !editor.isEditable || !hasTextAlign) {
            return null;
        }

        return (
            <DropdownMenu
                open={isOpen}
                onOpenChange={handleOpenChange}
            >
                <DropdownMenuTrigger asChild>
                    <Button
                        type="button"
                        data-style="ghost"
                        data-active-state={isActive ? 'on' : 'off'}
                        role="button"
                        tabIndex={-1}
                        aria-label="Text Alignment"
                        tooltip="Text Alignment"
                        {...buttonProps}
                        ref={ref}
                    >
                        <TriggerIcon className="tiptap-button-icon" />
                        <ChevronDownIcon className="tiptap-button-dropdown-small" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent portal={portal}>
                    <Card>
                        <CardBody>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.125rem' }}>
                                {alignments.map(align => (
                                    <DropdownMenuItem
                                        key={align}
                                        asChild
                                    >
                                        <TextAlignButton
                                            editor={editor}
                                            align={align}
                                            shortcutKeys={TEXT_ALIGN_SHORTCUT_KEYS[align]}
                                        />
                                    </DropdownMenuItem>
                                ))}
                            </div>
                        </CardBody>
                    </Card>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    },
);

TextAlignDropdownMenu.displayName = 'TextAlignDropdownMenu';

export default TextAlignDropdownMenu;
